import { keyCodes } from 'keyCodes';
import {
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
} from 'react';
import { dispatchAction } from '@ardoq/rxbeach';
import { isEditableElement } from '@ardoq/common-helpers';
import {
  closeSlideOverview,
  nextSlide,
  previousSlide,
  setIsNavigatorDismissed,
} from './actions';
import {
  PRESENTATION_NAVIGATOR_SELECTOR,
  SLIDE_OVERVIEW_PANEL_ID,
} from './atoms';
import { TOUR_HOST_TAG_NAME } from '@ardoq/product-tours';
import { PRESENTATION_CHROME_SELECTOR } from 'presentation/chrome';

const INTERACTIVE_ELEMENT_SELECTOR =
  'a[href], button, input, select, summary, [role="button"], [role="switch"], [role="option"], [role="tab"]';

const ACTIVATION_KEY_CODES = new Set([keyCodes.enter, keyCodes.space]);

// Enter and Space are the activation keys of whatever has focus, and
// preventDefault() on keydown suppresses that activation, so the slide
// navigation shortcuts have to stay out of the way of the chrome's controls.
// Controls rendered by the slide itself keep the shortcuts, which a presenter
// expects to work wherever focus happens to sit on the slide.
/**
 * The tour card is portalled into the browser's top layer, which lives on
 * `document.body` rather than inside the chrome, so it needs naming
 * separately even though it is one of the viewer's own controls.
 */
const isInsidePresentationControls = (target: Element): boolean =>
  Boolean(target.closest(PRESENTATION_CHROME_SELECTOR)) ||
  Boolean(target.closest(TOUR_HOST_TAG_NAME));

const isActivatingFocusedChromeControl = (event: KeyboardEvent): boolean => {
  if (!ACTIVATION_KEY_CODES.has(event.keyCode)) return false;
  const { target } = event;
  return (
    target instanceof Element &&
    target.matches(':focus-visible') &&
    Boolean(target.closest(INTERACTIVE_ELEMENT_SELECTOR)) &&
    isInsidePresentationControls(target)
  );
};

const SLIDE_OVERVIEW_PANEL_SELECTOR = `#${SLIDE_OVERVIEW_PANEL_ID}`;

const isInsideNavigator = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  Boolean(
    target.closest(PRESENTATION_NAVIGATOR_SELECTOR) ??
    target.closest(SLIDE_OVERVIEW_PANEL_SELECTOR)
  );

export const useCloseSlideOverviewOnOutsideClick = (
  isSlideOverviewOpen: boolean
) => {
  const isSlideOverviewOpenRef = useRef(isSlideOverviewOpen);

  useEffect(() => {
    isSlideOverviewOpenRef.current = isSlideOverviewOpen;
  }, [isSlideOverviewOpen]);

  useEffect(() => {
    const listenForOutsideClick = ({ target }: MouseEvent) => {
      if (!isSlideOverviewOpenRef.current) return;
      if (isInsideNavigator(target)) return;
      dispatchAction(closeSlideOverview());
    };
    document.addEventListener('click', listenForOutsideClick, true);

    return () => {
      document.removeEventListener('click', listenForOutsideClick, true);
    };
  }, []);
};

export const usePresentationKeyboardListener = (
  toggleDescription: VoidFunction | null
) => {
  const toggleDescriptionRef = useRef(toggleDescription);

  useEffect(() => {
    toggleDescriptionRef.current = toggleDescription;
  }, [toggleDescription]);

  useEffect(() => {
    const keyCodeActionMap = new Map([
      [keyCodes.rightArrow, () => dispatchAction(nextSlide())],
      [keyCodes.leftArrow, () => dispatchAction(previousSlide())],
      [keyCodes.space, () => dispatchAction(nextSlide())],
      [keyCodes.enter, () => dispatchAction(nextSlide())],
    ]);
    const listenForKeydown = (event: KeyboardEvent) => {
      // If event target is an input field/editable element, e.g. login, do nothing.
      if (isEditableElement(event.target as Node)) return;
      if (isActivatingFocusedChromeControl(event)) return;
      if (event.keyCode === keyCodes.escape) {
        dispatchAction(closeSlideOverview());
        return;
      }
      if (event.keyCode === keyCodes.letterD) {
        const toggleDescription = toggleDescriptionRef.current;
        if (!toggleDescription) return;
        toggleDescription();
        event.preventDefault();
        return;
      }
      const action = keyCodeActionMap.get(event.keyCode);
      if (action) {
        action();
        event.preventDefault();
      }
    };
    document.addEventListener('keydown', listenForKeydown);

    return () => document.removeEventListener('keydown', listenForKeydown);
  }, []);
};

// A click synthesised from Enter or Space carries a detail of 0, while a
// pointer click carries its click count. Only keyboard activation needs focus
// moved onto the replacement control; a pointer user never had focus there.
const isKeyboardActivation = (
  event: ReactMouseEvent<HTMLButtonElement>
): boolean => event.detail === 0;

type NavigatorDismissal = {
  showNavigatorRef: RefObject<HTMLButtonElement | null>;
  hideNavigatorRef: RefObject<HTMLButtonElement | null>;
  hideNavigator: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  showNavigator: (event: ReactMouseEvent<HTMLButtonElement>) => void;
};

export const useNavigatorDismissal = (
  isNavigatorDismissed: boolean
): NavigatorDismissal => {
  const showNavigatorRef = useRef<HTMLButtonElement>(null);
  const hideNavigatorRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreFocusRef = useRef(false);

  useEffect(() => {
    if (!shouldRestoreFocusRef.current) return;
    shouldRestoreFocusRef.current = false;
    const replacement = isNavigatorDismissed
      ? showNavigatorRef.current
      : hideNavigatorRef.current;
    replacement?.focus();
  }, [isNavigatorDismissed]);

  const setIsDismissed =
    (isDismissed: boolean) => (event: ReactMouseEvent<HTMLButtonElement>) => {
      shouldRestoreFocusRef.current = isKeyboardActivation(event);
      dispatchAction(setIsNavigatorDismissed(isDismissed));
    };

  return {
    showNavigatorRef,
    hideNavigatorRef,
    hideNavigator: setIsDismissed(true),
    showNavigator: setIsDismissed(false),
  };
};
